import { useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Heading, Icon, Paragraph, Switch, Toast, UpsellBanner, useToast } from '@zillow/constellation-10';
import { IconTagDollarSignFilled } from '@zillow/constellation-icons';

import { useRentalManagerUserPropertyEditWebApiHandler_displayTotalMonthlyPriceWithFee } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useGetListingDetails } from '../../../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../../../hooks/usePropertyEditMutation';
import stringHelper from '../../../../../utils/stringHelper';

import MFTotalMonthlyPriceWithFeeAcknowledgementModal from '../../../../modals/MFTotalMonthlyPriceWithFeeAcknowledgementModal';

import { EVENT_IDS as MF_COSTS_AND_FEES_EVENT_IDS } from '../../../../../analytics/events/multiFamilyCostsAndFees';
import {
  emitCloseTotalPriceModalEvent,
  emitLearnMoreEvent,
  emitRegulationsCTAEvent,
  emitTotalPriceToggleOffToOnEvent,
  emitTotalPriceToggleOnToOffEvent,
  EVENT_IDS,
} from '../../../../../analytics/events/multiFamilyTotalMonthlyPrice';
import { useAuth } from '../../../../AuthProvider';

const GENERIC = 'Learn more';
const FEE_TRANSPARENCY_LAWS = 'fee transparency laws';

type Props = {
  handleAddFeeClick: ({ triggerObject }: { triggerObject?: string }) => void;
  disabled?: boolean;
};

const MFCostAndFeeTotalMonthlyPriceWithFeeAlert = ({ handleAddFeeClick, disabled }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const { data: listingDetails, isFetching: isFetchingListingDetails } = useGetListingDetails(listingAlias);

  const displayTotalMonthlyPriceWithFee = listingDetails?.displayTotalMonthlyPriceWithFee;
  const totalMonthlyPriceWithFeeNotice = listingDetails?.totalMonthlyPriceWithFeeNotice;

  const [
    displayMFTotalMonthlyPriceWithFeeAcknowledgementModal,
    setDisplayMFTotalMonthlyPriceWithFeeAcknowledgementModal,
  ] = useState(false);

  const { mutateAsync: setDisplayTotalMonthlyPriceWithFee, isPending: isSubmitting } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_displayTotalMonthlyPriceWithFee,
    skipToast: true, // Using specific message based on change made
  });

  const onClickSwitchHandler = async (
    event: React.MouseEvent<HTMLInputElement, MouseEvent> & { target: { checked: boolean } },
  ) => {
    const valueToBe = event.target.checked;

    if (valueToBe) {
      if (listingDetails && user) {
        emitTotalPriceToggleOffToOnEvent({
          currentProperty: listingDetails,
          user,
          triggerObject:
            (totalMonthlyPriceWithFeeNotice?.mappings?.length || 0) > 1
              ? 'feature_promotion_toggle_geo'
              : 'feature_promotion_toggle_generic',
        });
      }
    } else {
      if (listingDetails && user) {
        emitTotalPriceToggleOnToOffEvent({
          currentProperty: listingDetails,
          user,
          triggerObject:
            (totalMonthlyPriceWithFeeNotice?.mappings?.length || 0) > 1
              ? 'feature_promotion_toggle_geo'
              : 'feature_promotion_toggle_generic',
        });
      }
    }

    try {
      await setDisplayTotalMonthlyPriceWithFee({
        getParams: { listingAlias, displayTotalMonthlyPriceWithFee: valueToBe },
      });

      if (valueToBe) {
        setDisplayMFTotalMonthlyPriceWithFeeAcknowledgementModal(true);
      }

      enqueueToast(
        <Toast tone="success">
          Required monthly fees {valueToBe ? 'included in' : 'excluded from'} total monthly price
        </Toast>,
      );
    } catch (error) {
      enqueueToast(
        <Toast tone="critical">
          {error.message || 'This setting could not be saved. Refresh the page and try again.'}
        </Toast>,
      );
    }
  };

  return (
    <>
      <Box
        data-testid="MFCostAndFeeTotalMonthlyPriceWithFeeAlert"
        css={{
          marginBottom: '400',
          border: '1px solid {colors.border.muted}',
          borderRadius: 'obj.default',
          overflow: 'hidden',
        }}
      >
        <UpsellBanner.Root tone="soft">
          <UpsellBanner.Media>
            <Icon render={<IconTagDollarSignFilled />} />
          </UpsellBanner.Media>

          <UpsellBanner.Content>
            <UpsellBanner.Header>
              <Heading level={6}>Include required monthly fees in total monthly price</Heading>

              <Paragraph>
                {totalMonthlyPriceWithFeeNotice &&
                  stringHelper.renderWithReplacedLinks(
                    totalMonthlyPriceWithFeeNotice,
                    {
                      callbacks: {
                        [GENERIC]: () => {
                          if (listingDetails && user) {
                            emitLearnMoreEvent({
                              currentProperty: listingDetails,
                              user,
                              triggerObject:
                                (totalMonthlyPriceWithFeeNotice?.mappings?.length || 0) > 1
                                  ? 'feature_promotion_toggle_geo'
                                  : 'feature_promotion_toggle_generic',
                            });
                          }
                        },
                        [FEE_TRANSPARENCY_LAWS]: () => {
                          if (listingDetails && user) {
                            emitRegulationsCTAEvent({
                              currentProperty: listingDetails,
                              user,
                            });
                          }
                        },
                      },
                      dataEventIds: {
                        [GENERIC]: EVENT_IDS.LEARN_MORE,
                        [FEE_TRANSPARENCY_LAWS]: EVENT_IDS.REGULATIONS_CTA,
                      },
                    },
                    GENERIC,
                  )}
              </Paragraph>
            </UpsellBanner.Header>
          </UpsellBanner.Content>

          <UpsellBanner.Action>
            <Switch
              data-testid="display-total-monthly-price-with-fee-switch"
              size="md"
              disabled={disabled || isSubmitting || isFetchingListingDetails}
              checked={displayTotalMonthlyPriceWithFee}
              onClick={onClickSwitchHandler}
              aria-label={`Include required monthly fees in total monthly price`}
              data-eventid={
                displayTotalMonthlyPriceWithFee
                  ? EVENT_IDS.TOTAL_PRICE_TOGGLE_ON_TO_OFF
                  : EVENT_IDS.TOTAL_PRICE_TOGGLE_OFF_TO_ON
              }
            />
          </UpsellBanner.Action>
        </UpsellBanner.Root>
      </Box>

      {displayMFTotalMonthlyPriceWithFeeAcknowledgementModal && (
        <MFTotalMonthlyPriceWithFeeAcknowledgementModal
          onClose={({ triggerSource }) => {
            if (listingDetails && user) {
              emitCloseTotalPriceModalEvent({
                currentProperty: listingDetails,
                user,
                triggerSource,
              });
            }

            setDisplayMFTotalMonthlyPriceWithFeeAcknowledgementModal(false);
          }}
          onSubmit={() => {
            handleAddFeeClick({ triggerObject: 'zram_costs_and_fees_tmp_modal' });
            setDisplayMFTotalMonthlyPriceWithFeeAcknowledgementModal(false);
          }}
          onCancelDataEventId={EVENT_IDS.CLOSE_TOTAL_PRICE_MODAL}
          onSubmitDataEventId={MF_COSTS_AND_FEES_EVENT_IDS.CLICK_ADD_MF_FEE}
        />
      )}
    </>
  );
};

export default MFCostAndFeeTotalMonthlyPriceWithFeeAlert;
